/* eslint-disable no-console */
import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { setFonemaIncomingRingingBusy } from '../../../services/fonemaCallBusyService';

/**
 * Hook for handling audio calls of WhatsApp using Fonema.
 *
 * @param {Object} params - Configuration parameters.
 * @param {string} params.token - Authentication token for the Fonema SDK.
 * @param {string} params.defaultFrom - Default origin phone number.
 * @param {boolean} params.isFonemaEnabled - Indicates if Fonema is enabled for this retailer.
 * @param {boolean} [params.canReceiveCalls=true] - Whether this user may accept incoming calls.
 *   Does not gate SDK init or phone registration; checked when an incoming call arrives.
 *
 * @returns {Object} Returns the state and actions of the hook.
 * @returns {boolean} return.sdkReady - Indicates if the SDK is ready to be used.
 * @returns {boolean} return.isAvailable - Indicates if the user is available to receive calls.
 *   When it is false, the incoming call handler returns null and no IncomingCall is shown (prevents concurrent calls
 *   and allows disabling reception). To enable/disable receiving calls from the UI, use setIsAvailable.
 * @returns {function(boolean)} return.setIsAvailable - Setter to enable (true) or disable (false) receiving calls.
 * @returns {string} return.from - Current origin phone number.
 * @returns {function} return.setFrom - Setter to change the origin phone number.
 */
export default function useFonemaWhatsappAudio({ token, defaultFrom, isFonemaEnabled, canReceiveCalls = true }) {
  const [sdkReady, setSdkReady] = useState(false);
  const [isAvailable, setIsAvailable] = useState(canReceiveCalls);
  const [from, setFrom] = useState(defaultFrom);
  const isAvailableRef = useRef(isAvailable);
  const canReceiveCallsRef = useRef(canReceiveCalls);
  // State to show one or several incoming calls
  // Now we store complete call objects (callData: { callId, callerNumber })
  const [incomingPhones, setIncomingPhones] = useState([]);
  const incomingPhonesRef = useRef(incomingPhones);
  useEffect(() => { incomingPhonesRef.current = incomingPhones; }, [incomingPhones]);

  useEffect(() => {
    setFonemaIncomingRingingBusy((incomingPhones?.length ?? 0) > 0);
  }, [incomingPhones]);

  useEffect(() => () => setFonemaIncomingRingingBusy(false), []);

  // Function to clean up call references (end of call cycle)
  const clearCallReferences = useCallback(() => {
    // Mark as available for new calls
    setIsAvailable(true);

    // Clean incoming phones
    setIncomingPhones([]);

    // Stop any ringing audio
    if (window.stopRingtone) {
      window.stopRingtone();
    }
  }, []);

  // Function to handle when an incoming call is answered:
  // - Clean all incoming calls from the list
  // - Stop the ringing audio
  // - NO change the availability (we continue in the active call)
  const handleIncomingCallAnswered = useCallback(() => {
    // Clean incoming phones
    setIncomingPhones([]);

    if (window.stopRingtone) {
      window.stopRingtone();
    }
  }, []);

  // Expose functions globally for use from other places
  useEffect(() => {
    window.clearFonemaCallReferences = clearCallReferences;
    window.handleFonemaIncomingCallAnswered = handleIncomingCallAnswered;
    return () => {
      delete window.clearFonemaCallReferences;
      delete window.handleFonemaIncomingCallAnswered;
    };
  }, [clearCallReferences, handleIncomingCallAnswered]);

  useEffect(() => {
    isAvailableRef.current = isAvailable;
  }, [isAvailable]);

  useEffect(() => {
    canReceiveCallsRef.current = canReceiveCalls;
    if (!canReceiveCalls) {
      setIsAvailable(false);
      setIncomingPhones([]);
      if (window.stopRingtone) {
        window.stopRingtone();
      }
    } else {
      setIsAvailable(true);
    }
  }, [canReceiveCalls]);

  // Always register the phone with the SDK. Permission to receive is checked when a call arrives.
  const registerIncomingCallHandler = useCallback(async ({ fromNumber }) => {
    if (!window.FonemaWhatsappAudio?.registerIncomingCallHandler) return;
    await window.FonemaWhatsappAudio.registerIncomingCallHandler(
      fromNumber,
      (callData) => {
        if (!isAvailableRef.current || !canReceiveCallsRef.current) {
          return null;
        }

        // Prevent duplicates of the same call by callId
        const exists = incomingPhonesRef.current.some(
          (existingCall) => existingCall.callId === callData.callId
        );
        if (!exists) {
          setIncomingPhones((prev) => [...prev, { ...callData, callDirection: 'incoming', registerNumber: fromNumber }]);
        }

        return { elementId: 'incoming-call', html: '.' };
      }
    );
  }, []);

  useEffect(() => {
    const initializeSDK = async () => {
      if (typeof window.FonemaWhatsappAudio !== "undefined" && window.FonemaWhatsappAudio.init) {
        try {
          await window.FonemaWhatsappAudio.init({ token });
          setSdkReady(true);
          if (defaultFrom) {
            await registerIncomingCallHandler({ fromNumber: defaultFrom });
          }
        } catch (error) {
          console.error("Error initializing Fonema Whatsapp Audio SDK:", error);
        }
      } else {
        setTimeout(initializeSDK, 1000);
      }
    };
    const timer = isFonemaEnabled ? setTimeout(initializeSDK, 500) : null;
    return () => {
      if (window?.FonemaWhatsappAudio?.removeIncomingCallHandler) {
        window.FonemaWhatsappAudio.removeIncomingCallHandler(defaultFrom);
      }
      clearTimeout(timer);
    };
  }, [token, defaultFrom, isFonemaEnabled, registerIncomingCallHandler]);

  const removeIncomingPhone = useCallback((callId) => {
    setIncomingPhones((prev) => prev.filter((call) => call.callId !== callId));
  }, []);

  /**
   * Update a record of `incomingPhones` by `callId`.
   * - `updatesOrUpdater` can be:
   *   - a "patch" object to merge over the call
   *   - or a function (prevCall) => nextCall
   */
  const updateIncomingPhone = useCallback((callId, updatesOrUpdater) => {
    const exists = incomingPhonesRef.current.some((call) => call.callId === callId);
    if (!exists) return false;

    setIncomingPhones((prev) => prev.map((call) => {
      if (call.callId !== callId) return call;
      if (typeof updatesOrUpdater === 'function') return updatesOrUpdater(call);
      return { ...call, ...(updatesOrUpdater ?? {}) };
    }));

    return true;
  }, []);

  return {
    /**
     * Incoming calls (array of callData, empty if there are no calls)
     */
    incomingPhones,
    /**
     * Setter to hide a specific IncomingCall (must be called when answering or declining)
     */
    setIncomingPhones,
    /**
     * Remove a call from the list of incoming calls by callId.
     */
    removeIncomingPhone,
    /**
     * Update a call from the list of incoming calls by callId.
     */
    updateIncomingPhone,
    /**
     * Function to clean up all call references
     */
    clearCallReferences,
    /**
     * Indicates if the SDK is ready to be used.
     * @type {boolean}
     */
    sdkReady,
    /**
     * Indicates if the user is available to receive calls.
     * @type {boolean}
     */
    isAvailable,
    /**
     * Setter to change the availability.
     * @type {function(boolean):void}
     */
    setIsAvailable,
    /**
     * Current origin phone number.
     * @type {string}
     */
    from,
    /**
     * Setter to change the origin phone number.
     * @type {function(string):void}
     */
    setFrom
  };
}
